import axios, { AxiosInstance } from 'axios'
import type { Draft, DraftSummary, Artifact, ArtifactKind, ArtifactTree, ArtifactTreeNode, Crew, Agent, ApiResponse, ArtifactCover, PlatformConfig, WechatConfig, ShareCreateResponse, ShareViewData, ShareType, RechargePlan, RechargeOrder, RechargeOrderStatus, RechargePayResult, PaymentMethod, UserBalance, TransactionListResponse, AdminRechargePlan, ShowcaseData, EvaluationPlan, EvaluationJob, EvaluationRun, EvaluationJobListResponse, EvaluationImportPreviewResponse, EvaluationRunListResponse, EvaluationJobStatus, EvaluationRunStatus, ArtifactImageGenerationRequest, ArtifactImageGenerationResult } from '../types'

const API_BASE = import.meta.env.VITE_API_BASE || 'http://localhost:8080'
const DEFAULT_IMAGE_UPLOAD_PATHS = ['/api/uploads/images', '/api/upload/image', '/api/files/images']
const ARTIFACT_IMAGE_GENERATION_TIMEOUT_MS = 10 * 60 * 1000

type AuthLoginMode = 'phone' | 'wechat' | 'hybrid'

class APIClient {
  private client: AxiosInstance

  private buildAdminConfig(adminKey?: string) {
    const key = (adminKey || '').trim()
    if (!key) {
      return undefined
    }
    return {
      headers: {
        'X-Admin-Key': key,
      },
    }
  }

  private normalizeAssetURL(urlOrPath: string): string {
    const value = (urlOrPath || '').trim()
    if (!value) return ''

    if (/^https?:\/\//i.test(value) || value.startsWith('//') || value.startsWith('data:') || value.startsWith('blob:')) {
      return value
    }

    try {
      return new URL(value, API_BASE).toString()
    } catch {
      return value
    }
  }

  private extractUploadURL(payload: any): string {
    const candidates = [payload?.data?.url, payload?.data?.path, payload?.data?.image_url, payload?.data?.image_path, payload?.data?.file_url, payload?.data?.file_path, payload?.result?.url, payload?.result?.path, payload?.url, payload?.path, payload?.image_url, payload?.image_path, payload?.file_url, payload?.file_path]

    const matched = candidates.find((item) => typeof item === 'string' && item.trim().length > 0)
    if (matched) {
      return matched
    }

    if (typeof payload?.data === 'string' && payload.data.trim().length > 0) {
      return payload.data
    }

    return ''
  }

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // 请求拦截器（自动添加token）
    this.client.interceptors.request.use(
      (config) => {
        const accessToken = localStorage.getItem('access_token')
        if (accessToken) {
          config.headers.Authorization = `Bearer ${accessToken}`
        }
        return config
      },
      (error) => Promise.reject(error),
    )

    // 响应拦截器（统一错误处理和自动刷新token）
    let isRefreshing = false
    let failedQueue: Array<{
      resolve: (value?: any) => void
      reject: (error?: any) => void
    }> = []

    const processQueue = (error: any, token: string | null = null) => {
      failedQueue.forEach((prom) => {
        if (error) {
          prom.reject(error)
        } else {
          prom.resolve(token)
        }
      })
      failedQueue = []
    }

    this.client.interceptors.response.use(
      (response) => {
        // 检查响应头中的 token 过期时间
        const expiresIn = response.headers['x-token-expires-in']
        if (expiresIn && parseInt(expiresIn) < 300) {
          // 剩余时间少于5分钟，自动刷新（但不阻塞当前请求）
          this.refreshTokenIfNeeded().catch((err) => {
            console.warn('Auto refresh token failed:', err)
            // 如果刷新失败，触发登出事件
            localStorage.removeItem('access_token')
            localStorage.removeItem('refresh_token')
            window.dispatchEvent(
              new CustomEvent('auth:logout', {
                detail: { reason: 'auto_refresh_failed' },
              }),
            )
          })
        }
        return response
      },
      async (error) => {
        const originalRequest = error.config
        const status = error.response?.status
        const message = String(error.response?.data?.message || '')

        // 已经重试过仍然401：判定为登录态失效，直接退出
        if (status === 401 && originalRequest?._retry && !originalRequest.url?.includes('/auth/refresh')) {
          localStorage.removeItem('access_token')
          localStorage.removeItem('refresh_token')
          window.dispatchEvent(
            new CustomEvent('auth:logout', {
              detail: { reason: 'unauthorized_after_retry' },
            }),
          )
          return Promise.reject(error)
        }

        // 兼容旧后端行为：用户不存在也视为鉴权失效，统一回登录页
        if (status === 404 && message.includes('用户不存在')) {
          localStorage.removeItem('access_token')
          localStorage.removeItem('refresh_token')
          window.dispatchEvent(
            new CustomEvent('auth:logout', {
              detail: { reason: 'user_not_found' },
            }),
          )
          return Promise.reject(error)
        }

        // 如果是401错误且不是刷新token的请求
        if (status === 401 && !originalRequest._retry && !originalRequest.url?.includes('/auth/refresh')) {
          if (isRefreshing) {
            // 如果正在刷新，将请求加入队列
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject })
            })
              .then((token) => {
                originalRequest.headers.Authorization = `Bearer ${token}`
                return this.client(originalRequest)
              })
              .catch((err) => {
                return Promise.reject(err)
              })
          }

          originalRequest._retry = true
          isRefreshing = true

          try {
            const newTokens = await this.refreshTokenIfNeeded()
            if (newTokens) {
              processQueue(null, newTokens.accessToken)
              originalRequest.headers.Authorization = `Bearer ${newTokens.accessToken}`
              return this.client(originalRequest)
            } else {
              processQueue(new Error('Failed to refresh token'), null)
              throw error
            }
          } catch (refreshError) {
            processQueue(refreshError, null)
            // 刷新失败，清除token，触发登出事件
            localStorage.removeItem('access_token')
            localStorage.removeItem('refresh_token')
            // 触发自定义事件，通知 App 组件跳转到登录页
            window.dispatchEvent(
              new CustomEvent('auth:logout', {
                detail: { reason: 'token_refresh_failed' },
              }),
            )
            console.warn('Token refresh failed, redirecting to login')
            return Promise.reject(refreshError)
          } finally {
            isRefreshing = false
          }
        }

        return Promise.reject(error)
      },
    )
  }

  // Draft API
  async createDraft(crewId: string, requirement?: string): Promise<Draft> {
    const { data } = await this.client.post<ApiResponse<Draft>>('/api/drafts', {
      crew_id: crewId,
      requirement: requirement || undefined, // 可选字段，适配后端字段名
    })
    return data.data!
  }

  async createArtifact(
    draftId: string,
    payload: {
      title?: string
      content?: string
      description?: string
      kind?: ArtifactKind
      cover?: ArtifactCover
    },
  ): Promise<Artifact> {
    const { data } = await this.client.post<ApiResponse<Artifact>>(`/api/drafts/${draftId}/artifacts`, payload)
    return data.data!
  }

  async listDrafts(params: { limit?: number; offset?: number } = {}): Promise<DraftSummary[]> {
    const limit = params.limit ?? 20
    const offset = params.offset ?? 0
    const { data } = await this.client.get<ApiResponse<DraftSummary[]>>('/api/drafts', {
      params: { limit, offset },
    })
    return data.data || []
  }

  async getDraft(draftId: string): Promise<Draft> {
    const { data } = await this.client.get<ApiResponse<Draft>>(`/api/drafts/${draftId}`)
    return data.data!
  }

  async approveDraft(draftId: string, data?: Record<string, any>): Promise<void> {
    await this.client.post(`/api/drafts/${draftId}/approve`, {
      interaction_data: data,
    })
  }

  async rejectDraft(draftId: string, comment: string): Promise<void> {
    await this.client.post(`/api/drafts/${draftId}/reject`, {
      comment: comment,
    })
  }

  async retryDraft(draftId: string): Promise<void> {
    await this.client.post(`/api/drafts/${draftId}/retry`)
  }

  async startDynamicTask(draftId: string, prompt: string): Promise<{ task_id: string; status: string }> {
    const { data } = await this.client.post<ApiResponse<{ task_id: string; status: string }>>(`/api/drafts/${draftId}/dynamic-task`, { prompt })
    return data.data!
  }

  async stopDraft(draftId: string): Promise<void> {
    await this.client.post(`/api/drafts/${draftId}/stop`)
  }

  async createPublishContent(
    draftId: string,
    payload: {
      artifact_id: string
      platform_id?: string
      title?: string
      content?: string
      content_format?: 'markdown' | 'html'
      cover_url?: string
      author?: string
      digest?: string
      extra?: Record<string, any>
    },
  ): Promise<{ content_id: string }> {
    const { data } = await this.client.post<ApiResponse<{ content_id: string }>>(`/api/drafts/${draftId}/publish-contents`, payload)
    return data.data!
  }

  async deleteDraft(draftId: string): Promise<void> {
    await this.client.delete(`/api/drafts/${draftId}`)
  }

  async deleteArtifact(draftId: string, artifactId: string): Promise<void> {
    await this.client.delete(`/api/drafts/${draftId}/artifacts/${artifactId}`)
  }

  async updateArtifact(
    draftId: string,
    artifactId: string,
    updates: {
      title?: string
      content?: string
      description?: string
      cover?: ArtifactCover
    },
  ): Promise<void> {
    await this.client.patch(`/api/drafts/${draftId}/artifacts/${artifactId}`, updates)
  }

  async generateArtifactImage(draftId: string, artifactId: string, payload: ArtifactImageGenerationRequest): Promise<ArtifactImageGenerationResult> {
    const { data } = await this.client.post<ApiResponse<ArtifactImageGenerationResult>>(
      `/api/drafts/${draftId}/artifacts/${artifactId}/image-generations`,
      payload,
      { timeout: ARTIFACT_IMAGE_GENERATION_TIMEOUT_MS },
    )
    return data.data!
  }

  async getArtifactTree(): Promise<ArtifactTree> {
    const { data } = await this.client.get<ApiResponse<ArtifactTree>>('/api/artifact-tree')
    return data.data || { nodes: [] }
  }

  async createArtifactTreeFolder(payload: { name: string; parent_id?: number | null; sort_order?: number }): Promise<ArtifactTreeNode> {
    const { data } = await this.client.post<ApiResponse<ArtifactTreeNode>>('/api/artifact-tree/folders', payload)
    return data.data!
  }

  async attachArtifactToTree(payload: { artifact_id: string; parent_id?: number | null; sort_order?: number }): Promise<ArtifactTreeNode> {
    const { data } = await this.client.post<ApiResponse<ArtifactTreeNode>>('/api/artifact-tree/attach', payload)
    return data.data!
  }

  async updateArtifactTreeNode(
    nodeId: number,
    payload: {
      name?: string
      parent_id?: number | null
      sort_order?: number
    },
  ): Promise<ArtifactTreeNode> {
    const { data } = await this.client.patch<ApiResponse<ArtifactTreeNode>>(`/api/artifact-tree-nodes/${nodeId}`, payload)
    return data.data!
  }

  async deleteArtifactTreeNode(nodeId: number): Promise<void> {
    await this.client.delete(`/api/artifact-tree-nodes/${nodeId}`)
  }

  async uploadImage(file: File): Promise<string> {
    if (!file) {
      throw new Error('未找到图片文件')
    }

    const configuredPath = import.meta.env.VITE_IMAGE_UPLOAD_PATH?.trim()
    const uploadPaths = configuredPath ? [configuredPath] : DEFAULT_IMAGE_UPLOAD_PATHS
    let lastError: any = null

    for (const uploadPath of uploadPaths) {
      try {
        const formData = new FormData()
        formData.append('file', file)

        const { data } = await this.client.post(uploadPath, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })

        const rawURL = this.extractUploadURL(data)
        if (!rawURL) {
          throw new Error('上传接口未返回图片地址')
        }

        return this.normalizeAssetURL(rawURL)
      } catch (error: any) {
        lastError = error
        const status = error?.response?.status
        const shouldFallback = !configuredPath && status === 404
        if (!shouldFallback) {
          break
        }
      }
    }

    if (lastError) {
      throw lastError
    }

    throw new Error('图片上传失败')
  }

  async rehostImageURL(url: string): Promise<string> {
    const { data } = await this.client.post<ApiResponse<{ url: string }>>('/api/uploads/rehost', { url })
    return data.data!.url
  }

  // Share API
  async createShare(params: { draft_id: string; share_type: ShareType; artifact_id?: string }): Promise<ShareCreateResponse> {
    const { data } = await this.client.post<ApiResponse<ShareCreateResponse>>('/api/shares', params)
    return data.data!
  }

  async getShare(shareId: string): Promise<ShareViewData> {
    const { data } = await this.client.get<ApiResponse<ShareViewData>>(`/api/shares/${shareId}`)
    return data.data!
  }

  async getExistingShare(params: { draft_id: string; share_type: ShareType; artifact_id?: string }): Promise<ShareCreateResponse> {
    const query = new URLSearchParams()
    query.append('draft_id', params.draft_id)
    query.append('share_type', params.share_type)
    if (params.artifact_id) {
      query.append('artifact_id', params.artifact_id)
    }
    const { data } = await this.client.get<ApiResponse<ShareCreateResponse>>(`/api/shares/existing?${query.toString()}`)
    return data.data!
  }

  async deleteShare(shareId: string): Promise<void> {
    await this.client.delete(`/api/shares/${shareId}`)
  }

  // Crew API
  async listCrews(): Promise<Crew[]> {
    const { data } = await this.client.get<ApiResponse<Crew[]>>('/api/crews')
    return data.data || []
  }

  // Admin Crew API
  async listAdminCrews(adminKey?: string): Promise<Crew[]> {
    const { data } = await this.client.get<ApiResponse<Crew[]>>('/admin/crews', this.buildAdminConfig(adminKey))
    return data.data || []
  }

  async getAdminCrew(crewId: string, adminKey?: string): Promise<Crew> {
    const { data } = await this.client.get<ApiResponse<Crew>>(`/admin/crews/${crewId}`, this.buildAdminConfig(adminKey))
    return data.data!
  }

  async createAdminCrew(payload: Partial<Crew>, adminKey?: string): Promise<Crew> {
    const { data } = await this.client.post<ApiResponse<Crew>>('/admin/crews', payload, this.buildAdminConfig(adminKey))
    return data.data!
  }

  async updateAdminCrew(crewId: string, payload: Partial<Crew>, adminKey?: string): Promise<Crew> {
    const { data } = await this.client.put<ApiResponse<Crew>>(`/admin/crews/${crewId}`, payload, this.buildAdminConfig(adminKey))
    return data.data!
  }

  async deleteAdminCrew(crewId: string, adminKey?: string): Promise<void> {
    await this.client.delete(`/admin/crews/${crewId}`, this.buildAdminConfig(adminKey))
  }

  // Admin Agent API
  async listAdminAgents(adminKey?: string): Promise<Agent[]> {
    const { data } = await this.client.get<ApiResponse<Agent[]>>('/admin/agents', this.buildAdminConfig(adminKey))
    return data.data || []
  }

  async getAdminAgent(agentId: string, adminKey?: string): Promise<Agent> {
    const { data } = await this.client.get<ApiResponse<Agent>>(`/admin/agents/${agentId}`, this.buildAdminConfig(adminKey))
    return data.data!
  }

  async createAdminAgent(payload: Partial<Agent>, adminKey?: string): Promise<Agent> {
    const { data } = await this.client.post<ApiResponse<Agent>>('/admin/agents', payload, this.buildAdminConfig(adminKey))
    return data.data!
  }

  async updateAdminAgent(agentId: string, payload: Partial<Agent>, adminKey?: string): Promise<Agent> {
    const { data } = await this.client.put<ApiResponse<Agent>>(`/admin/agents/${agentId}`, payload, this.buildAdminConfig(adminKey))
    return data.data!
  }

  async deleteAdminAgent(agentId: string, adminKey?: string): Promise<void> {
    await this.client.delete(`/admin/agents/${agentId}`, this.buildAdminConfig(adminKey))
  }

  async getRandomTopic(crewId: string, excludeTopic?: string): Promise<string> {
    const params = new URLSearchParams()
    if (excludeTopic) {
      params.append('topic', excludeTopic)
    }
    const queryString = params.toString()
    const url = `/api/crews/${crewId}/random-topic${queryString ? `?${queryString}` : ''}`
    const { data } = await this.client.get<ApiResponse<{ topic: string; source: string }>>(url)
    return data.data!.topic
  }

  // Auth API
  async getAuthMeta(): Promise<{
    login_mode: AuthLoginMode
    providers: AuthLoginMode[]
  }> {
    const { data } = await this.client.get<ApiResponse<{ login_mode: AuthLoginMode; providers: AuthLoginMode[] }>>('/api/auth/meta')
    return data.data!
  }

  async sendCode(phone: string): Promise<{ message: string; code?: string }> {
    const { data } = await this.client.post<ApiResponse<{ message: string; code?: string }>>('/api/auth/send-code', {
      phone,
    })
    return data.data!
  }

  async verifyInvite(code: string): Promise<{ valid: boolean; type: string }> {
    const { data } = await this.client.post<ApiResponse<{ valid: boolean; type: string }>>('/api/auth/verify-invite', {
      code,
    })
    return data.data!
  }

  async login(phone: string, code: string, inviteCode?: string): Promise<{ access_token: string; refresh_token: string; user: any }> {
    const { data } = await this.client.post<ApiResponse<{ access_token: string; refresh_token: string; user: any }>>('/api/auth/login', {
      phone,
      code,
      invite_code: inviteCode,
    })
    return data.data!
  }

  async testLogin(username: string, password: string): Promise<{ access_token: string; refresh_token: string; user: any }> {
    const { data } = await this.client.post<ApiResponse<{ access_token: string; refresh_token: string; user: any }>>('/api/auth/test-login', {
      username,
      password,
    })
    return data.data!
  }

  async exchangeWechatLoginTicket(loginTicket: string): Promise<{ access_token: string; refresh_token: string; user: any }> {
    const { data } = await this.client.post<ApiResponse<{ access_token: string; refresh_token: string; user: any }>>('/api/auth/wechat/exchange', {
      login_ticket: loginTicket,
    })
    return data.data!
  }

  getWechatAuthStartURL(inviteCode?: string): string {
    const baseURL = `${API_BASE.replace(/\/$/, '')}/api/auth/wechat/start`
    const normalizedInviteCode = inviteCode?.trim()
    if (!normalizedInviteCode) {
      return baseURL
    }

    const params = new URLSearchParams()
    params.set('invite_code', normalizedInviteCode)
    return `${baseURL}?${params.toString()}`
  }

  getAPIOrigin(): string {
    try {
      return new URL(API_BASE, window.location.origin).origin
    } catch {
      return window.location.origin
    }
  }

  async refreshToken(refreshToken: string): Promise<{ access_token: string; refresh_token: string }> {
    const { data } = await this.client.post<ApiResponse<{ access_token: string; refresh_token: string }>>('/api/auth/refresh', {
      refresh_token: refreshToken,
    })
    return data.data!
  }

  // 内部方法：如果需要则刷新token
  private async refreshTokenIfNeeded(): Promise<{
    accessToken: string
    refreshToken: string
  } | null> {
    const refreshToken = localStorage.getItem('refresh_token')
    if (!refreshToken) {
      return null
    }

    try {
      const { access_token, refresh_token } = await this.refreshToken(refreshToken)
      localStorage.setItem('access_token', access_token)
      localStorage.setItem('refresh_token', refresh_token)
      return { accessToken: access_token, refreshToken: refresh_token }
    } catch (error) {
      console.error('Failed to refresh token:', error)
      return null
    }
  }

  async logout(): Promise<void> {
    await this.client.post('/api/auth/logout')
  }

  async getProfile(): Promise<any> {
    const { data } = await this.client.get<ApiResponse<any>>('/api/user/profile')
    return data.data!
  }

  async updateProfile(updates: { display_name?: string; avatar_url?: string; email?: string }): Promise<any> {
    const { data } = await this.client.put<ApiResponse<any>>('/api/user/profile', updates)
    return data.data!
  }

  // Platform Stats（公开接口，无需登录）
  async getPlatformStats(): Promise<{
    user_count: number
    task_count: number
  }> {
    const { data } = await this.client.get<ApiResponse<{ user_count: number; task_count: number }>>('/api/stats')
    return data.data!
  }

  // Showcase（公开接口，首页展示）
  async getShowcase(): Promise<ShowcaseData> {
    const { data } = await this.client.get<ApiResponse<ShowcaseData>>('/api/showcase')
    return data.data!
  }

  // Settings API - 平台集成配置
  async listPlatformConfigs(): Promise<PlatformConfig[]> {
    const { data } = await this.client.get<ApiResponse<PlatformConfig[]>>('/api/settings/platforms')
    return data.data || []
  }

  async getPlatformConfig(platform: string): Promise<PlatformConfig | null> {
    const { data } = await this.client.get<ApiResponse<PlatformConfig>>(`/api/settings/platforms/${platform}`)
    return data.data || null
  }

  async saveWechatConfig(config: WechatConfig): Promise<void> {
    await this.client.post('/api/settings/platforms/wechat', config)
  }

  async testWechatConnection(): Promise<{ success: boolean; message: string }> {
    const { data } = await this.client.post<ApiResponse<{ success: boolean; message: string }>>('/api/settings/platforms/wechat/test')
    return data.data!
  }

  // ============ 充值相关 API ============

  // 获取用户余额
  async getUserBalance(): Promise<UserBalance> {
    const { data } = await this.client.get<ApiResponse<UserBalance>>('/api/user/balance')
    return data.data!
  }

  // 获取充值套餐列表
  async getRechargePlans(): Promise<RechargePlan[]> {
    const { data } = await this.client.get<ApiResponse<RechargePlan[]>>('/api/recharge/plans')
    return data.data || []
  }

  // 创建充值订单
  async createRechargeOrder(planCode: string, paymentMethod: PaymentMethod = 'wechat'): Promise<RechargeOrder> {
    const { data } = await this.client.post<ApiResponse<RechargeOrder>>('/api/recharge/orders', {
      plan_code: planCode,
      payment_method: paymentMethod,
    })
    return data.data!
  }

  // 查询订单支付状态（含后端主动查询微信状态）
  async getRechargeOrderStatus(orderId: number): Promise<RechargeOrderStatus> {
    const { data } = await this.client.get<ApiResponse<RechargeOrderStatus>>(`/api/recharge/orders/${orderId}/status`)
    return data.data!
  }

  // 沙箱支付（测试环境模拟支付成功）
  async sandboxPayOrder(orderId: number): Promise<RechargePayResult> {
    const { data } = await this.client.post<ApiResponse<RechargePayResult>>('/api/recharge/sandbox/pay', {
      order_id: orderId,
    })
    return data.data!
  }

  // 获取交易历史
  async getTokenTransactions(page: number = 1, pageSize: number = 20): Promise<TransactionListResponse> {
    const { data } = await this.client.get<ApiResponse<TransactionListResponse>>('/api/token/transactions', {
      params: { page, page_size: pageSize },
    })
    return data.data!
  }

  // ============ Admin Recharge Plans API ============

  async adminListPlans(adminKey?: string): Promise<AdminRechargePlan[]> {
    const { data } = await this.client.get<ApiResponse<AdminRechargePlan[]>>('/admin/recharge/plans', this.buildAdminConfig(adminKey))
    return data.data || []
  }

  async adminCreatePlan(
    payload: {
      plan_code: string
      name: string
      token_amount: number
      price: number
      tag?: string
      sort_order?: number
    },
    adminKey?: string,
  ): Promise<AdminRechargePlan> {
    const { data } = await this.client.post<ApiResponse<AdminRechargePlan>>('/admin/recharge/plans', payload, this.buildAdminConfig(adminKey))
    return data.data!
  }

  async adminUpdatePlan(
    id: number,
    payload: {
      name: string
      token_amount: number
      price: number
      tag?: string
      sort_order?: number
      is_active?: boolean
    },
    adminKey?: string,
  ): Promise<AdminRechargePlan> {
    const { data } = await this.client.put<ApiResponse<AdminRechargePlan>>(`/admin/recharge/plans/${id}`, payload, this.buildAdminConfig(adminKey))
    return data.data!
  }

  async adminTogglePlan(id: number, adminKey?: string): Promise<AdminRechargePlan> {
    const { data } = await this.client.patch<ApiResponse<AdminRechargePlan>>(`/admin/recharge/plans/${id}/toggle`, {}, this.buildAdminConfig(adminKey))
    return data.data!
  }

  // ============ Admin Evaluation API ============

  async createEvaluationJob(
    payload: {
      name: string
      description?: string
      plan: EvaluationPlan
    },
    adminKey?: string,
  ): Promise<{
    job_id: string
    status: EvaluationJobStatus
    total_runs: number
  }> {
    const { data } = await this.client.post<
      ApiResponse<{
        job_id: string
        status: EvaluationJobStatus
        total_runs: number
      }>
    >('/admin/evaluations/jobs', payload, this.buildAdminConfig(adminKey))
    return data.data!
  }

  async startEvaluationJob(jobId: string, adminKey?: string): Promise<{ job_id: string; status: EvaluationJobStatus }> {
    const { data } = await this.client.post<ApiResponse<{ job_id: string; status: EvaluationJobStatus }>>(`/admin/evaluations/jobs/${jobId}/start`, {}, this.buildAdminConfig(adminKey))
    return data.data!
  }

  async stopEvaluationJob(jobId: string, adminKey?: string): Promise<void> {
    await this.client.post(`/admin/evaluations/jobs/${jobId}/stop`, {}, this.buildAdminConfig(adminKey))
  }

  async listEvaluationJobs(
    params: {
      status?: EvaluationJobStatus
      page?: number
      page_size?: number
    } = {},
    adminKey?: string,
  ): Promise<EvaluationJobListResponse> {
    const { data } = await this.client.get<ApiResponse<EvaluationJobListResponse>>('/admin/evaluations/jobs', {
      ...this.buildAdminConfig(adminKey),
      params,
    })
    return data.data!
  }

  async getEvaluationJob(jobId: string, adminKey?: string): Promise<EvaluationJob> {
    const { data } = await this.client.get<ApiResponse<EvaluationJob>>(`/admin/evaluations/jobs/${jobId}`, this.buildAdminConfig(adminKey))
    return data.data!
  }

  async listEvaluationRuns(
    jobId: string,
    params: {
      status?: EvaluationRunStatus
      variant_id?: string
      case_id?: string
      page?: number
      page_size?: number
    } = {},
    adminKey?: string,
  ): Promise<EvaluationRunListResponse> {
    const { data } = await this.client.get<ApiResponse<EvaluationRunListResponse>>(`/admin/evaluations/jobs/${jobId}/runs`, {
      ...this.buildAdminConfig(adminKey),
      params,
    })
    return data.data!
  }

  async getEvaluationRun(runId: string, adminKey?: string): Promise<EvaluationRun> {
    const { data } = await this.client.get<ApiResponse<EvaluationRun>>(`/admin/evaluations/runs/${runId}`, this.buildAdminConfig(adminKey))
    return data.data!
  }

  async previewEvaluationImport(
    payload: {
      crew_id: string
      time_from?: string
      time_to?: string
      sample_strategy?: 'recent_n' | 'random_seeded' | 'stratified_by_status'
      sample_size?: number
      seed?: number
    },
    adminKey?: string,
  ): Promise<EvaluationImportPreviewResponse> {
    const { data } = await this.client.post<ApiResponse<EvaluationImportPreviewResponse>>('/admin/evaluations/import/preview', payload, this.buildAdminConfig(adminKey))
    return data.data!
  }

  async createEvaluationImportJob(
    payload: {
      name: string
      description?: string
      crew_id: string
      time_from?: string
      time_to?: string
      sample_strategy?: 'recent_n' | 'random_seeded' | 'stratified_by_status'
      sample_size?: number
      seed?: number
      judge: {
        model: string
        temperature?: number
        sample_count?: number
      }
      scoring?: {
        template?: string
      }
    },
    adminKey?: string,
  ): Promise<{
    job_id: string
    status: EvaluationJobStatus
    total_runs: number
  }> {
    const { data } = await this.client.post<
      ApiResponse<{
        job_id: string
        status: EvaluationJobStatus
        total_runs: number
      }>
    >('/admin/evaluations/import/jobs', payload, this.buildAdminConfig(adminKey))
    return data.data!
  }
}

export const apiClient = new APIClient()
